import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, Crown, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, attempts]);

  const checkPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Success Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {status === 'checking' && (
          <div className="text-center" data-testid="checking-status">
            <Loader2 className="h-16 w-16 text-sky-500 animate-spin mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Activating Your Subscription
            </h1>
            <p className="text-lg text-slate-600">
              Please wait while we activate your all-access subscription...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center" data-testid="success-status">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Welcome to All-Access!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Your subscription is now active. You have unlimited access to 1,200+ formulas!
            </p>

            <div className="bg-gradient-to-br from-sky-500 to-sky-600 text-white rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Crown className="h-8 w-8" />
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Your Benefits
                </h2>
              </div>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span>Instant access to all 1,200+ business formulas</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span>New formulas added monthly at no extra cost</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span>Priority support from our expert team</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span>Access to supplier directory</span>
                </li>
              </ul>
            </div>

            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                What's Next?
              </h3>
              <p className="text-slate-600 mb-4">
                Start exploring our complete formula library. Browse by category or search for specific products you want to manufacture.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/products" className="flex-1">
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full" data-testid="browse-formulas-btn">
                    Browse All Formulas
                  </Button>
                </Link>
                <Link to="/suppliers" className="flex-1">
                  <Button variant="outline" className="w-full rounded-full" data-testid="view-suppliers-btn">
                    View Suppliers
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Need Help Getting Started?
              </h3>
              <p className="text-slate-600 mb-4">
                Our support team is here to help you succeed. Contact us anytime via WhatsApp.
              </p>
              <a
                href="https://wa.me/27730758123"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full">
                  Contact Support on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center" data-testid="error-status">
            <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Subscription Activation Failed
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We encountered an error activating your subscription. Please contact support.
            </p>
            <Link to="/">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                Back to Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'timeout' && (
          <div className="text-center" data-testid="timeout-status">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Activation Taking Longer Than Expected
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Your subscription is being processed. Please check your email for confirmation or contact support.
            </p>
            <Link to="/">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                Back to Home
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
